import firebase from 'firebase/app';
import uniq from 'lodash/uniq';

import getTimestampObject from './../../helpers/getTimestampObject';
import updateUserDataInFirebase from './../../helpers/updateUserDataInFirebase';
import {canRead, canEdit, canCreate, canDelete, canViewAdminStuff} from './../../helpers/roleAuthorisation';

const namespaced = true;

const initState = {
  accountCreatedAt: null,
  accountLastModifiedAt: null,
  avatarToUse: null,
  country: null,
  customAvatarFilename: null,
  customAvatarOrientation: null,
  customAvatarOriginalFilesizeKb: null,
  customAvatarOriginalHeight: null,
  customAvatarOriginalWidth: null,
  customAvatarUrl: null,
  email: null,
  emailVerified: null,
  enabled: null,
  firstName: null,
  gamerTag: null,
  lastLoggedInAt: null,
  lastLoggedOutAt: null,
  lastName: null,
  numTimesLoggedIn: null,
  numTimesLoggedOut: null,
  overrideOsDarkMode: null,
  playSoundEffects: null,
  prefersColorScheme: null,
  prefersColorSchemeTemp: null,
  starredBoards: null,
  roles: null,
  userId: null,
  userIdShort: null,
  yearBorn: null,
};

const state = {...initState};

const getters = {
  getAccountCreatedAt: state => {
    return state.accountCreatedAt;
  },
  getEnabled: state => {
    return state.enabled;
  },
  getAccountLastModifiedAt: state => {
    return state.accountLastModifiedAt;
  },
  getAvatarToUse: state => {
    return state.avatarToUse;
  },
  getCountry: state => {
    return state.country;
  },
  getCustomAvatarFilename: state => {
    return state.customAvatarFilename;
  },
  getCustomAvatarOrientation: state => {
    return state.customAvatarOrientation || '';
  },
  getCustomAvatarOriginalFilesizeKb: state => {
    return state.customAvatarOriginalFilesizeKb || '';
  },
  getCustomAvatarOriginalHeight: state => {
    return state.customAvatarOriginalHeight || '';
  },
  getCustomAvatarOriginalWidth: state => {
    return state.customAvatarOriginalWidth || '';
  },
  getCustomAvatarUrl: state => {
    return state.customAvatarUrl;
  },
  getEmail: state => {
    return state.email;
  },
  getEmailVerified: state => {
    return state.emailVerified;
  },
  getFirstName: state => {
    return state.firstName;
  },
  getGamerTag: state => {
    return state.gamerTag;
  },
  getLastLoggedInAt: state => {
    return state.lastLoggedInAt;
  },
  getLastLoggedOutAt: state => {
    return state.lastLoggedOutAt;
  },
  getLastName: state => {
    return state.lastName;
  },
  getNumTimesLoggedIn: state => {
    return state.numTimesLoggedIn;
  },
  getNumTimesLoggedOut: state => {
    return state.numTimesLoggedOut;
  },
  getOverrideOsDarkMode: state => {
    return state.overrideOsDarkMode;
  },
  getPlaySoundEffects: state => {
    return state.playSoundEffects;
  },
  getPrefersColorScheme: state => {
    return state.prefersColorScheme;
  },
  getPrefersColorSchemeTemp: state => {
    return state.prefersColorSchemeTemp;
  },
  getRoles: state => {
    return state.roles;
  },
  getStarredBoards: state => {
    return state.starredBoards;
  },
  getUserId: state => {
    return state.userId;
  },
  getUserIdShort: state => {
    return state.userIdShort;
  },
  getYearBorn: state => {
    return state.yearBorn;
  },
  userCanRead: state => {
    return canRead(state.roles);
  },
  userCanEdit: state => {
    return canEdit(state.roles);
  },
  userCanCreate: state => {
    return canCreate(state.roles);
  },
  userCanDelete: state => {
    return canDelete(state.roles);
  },
  userCanViewAdminStuff: state => {
    return canViewAdminStuff(state.roles);
  },
};

const mutations = {
  SET_USER_DATA_FROM_FIREBASE_MUTATION: (state, payload) => {
    state.accountCreatedAt = payload.accountCreatedAt;
    state.accountLastModifiedAt = payload.accountLastModifiedAt;
    state.avatarToUse = payload.avatarToUse;
    state.country = payload.country;
    state.customAvatarFilename = payload.customAvatarFilename;
    state.customAvatarOrientation = payload.customAvatarOrientation;
    state.customAvatarOriginalFilesizeKb = payload.customAvatarOriginalFilesizeKb;
    state.customAvatarOriginalHeight = payload.customAvatarOriginalHeight;
    state.customAvatarOriginalWidth = payload.customAvatarOriginalWidth;
    state.customAvatarUrl = payload.customAvatarUrl;
    state.email = payload.email;
    state.emailVerified = payload.emailVerified;
    state.enabled = payload.enabled;
    state.firstName = payload.firstName;
    state.gamerTag = payload.gamerTag;
    state.lastLoggedInAt = payload.lastLoggedInAt;
    state.lastLoggedOutAt = payload.lastLoggedOutAt;
    state.lastName = payload.lastName;
    state.numTimesLoggedIn = payload.numTimesLoggedIn;
    state.numTimesLoggedOut = payload.numTimesLoggedOut;
    state.overrideOsDarkMode = payload.overrideOsDarkMode;
    state.playSoundEffects = payload.playSoundEffects;
    state.prefersColorScheme = payload.prefersColorScheme;
    state.roles = payload.roles;
    state.starredBoards = payload.starredBoards;
    state.userId = payload.userId;
    state.userIdShort = payload.userIdShort;
    state.yearBorn = payload.yearBorn;
  },
  SET_EMAIL_VERIFIED_MUTATION: (state, payload) => {
    state.emailVerified = payload;
    updateUserDataInFirebase({
      emailVerified: true,
    });
  },
  SET_LAST_LOGGED_IN_AT_MUTATION: (state, payload) => {
    state.lastLoggedInAt = payload;
    updateUserDataInFirebase({
      lastLoggedInAt: firebase.firestore.FieldValue.serverTimestamp(),
    });
  },
  SET_LAST_LOGGED_OUT_AT_MUTATION: (state, payload) => {
    state.lastLoggedOutAt = payload;
    updateUserDataInFirebase({
      lastLoggedOutAt: firebase.firestore.FieldValue.serverTimestamp(),
    });
  },
  CLEAR_USER_DATA_MUTATION: (state, payload) => {
    state.accountCreatedAt = payload.accountCreatedAt;
    state.accountLastModifiedAt = payload.accountLastModifiedAt;
    state.avatarToUse = payload.avatarToUse;
    state.country = payload.country;
    state.customAvatarFilename = payload.customAvatarFilename;
    state.customAvatarOrientation = payload.customAvatarOrientation;
    state.customAvatarOriginalFilesizeKb = payload.customAvatarOriginalFilesizeKb;
    state.customAvatarOriginalHeight = payload.customAvatarOriginalHeight;
    state.customAvatarOriginalWidth = payload.customAvatarOriginalWidth;
    state.customAvatarUrl = payload.customAvatarUrl;
    state.email = payload.email;
    state.emailVerified = payload.emailVerified;
    state.enabled = payload.enabled;
    state.firstName = payload.firstName;
    state.gamerTag = payload.gamerTag;
    state.lastLoggedInAt = payload.lastLoggedInAt;
    state.lastLoggedOutAt = payload.lastLoggedOutAt;
    state.lastName = payload.lastName;
    state.numTimesLoggedIn = payload.numTimesLoggedIn;
    state.numTimesLoggedOut = payload.numTimesLoggedOut;
    state.overrideOsDarkMode = payload.overrideOsDarkMode;
    state.playSoundEffects = payload.playSoundEffects;
    state.prefersColorScheme = payload.prefersColorScheme;
    state.prefersColorSchemeTemp = payload.prefersColorSchemeTemp;
    state.roles = payload.roles;
    state.userId = payload.userId;
    state.userIdShort = payload.userIdShort;
    state.yearBorn = payload.yearBorn;
  },
  SET_USER_PROFILE_DATA_MUTATION: (state, payload) => {
    state.avatarToUse = payload.avatarToUse;
    state.firstName = payload.firstName;
    state.gamerTag = payload.gamerTag;
    state.lastName = payload.lastName;
    state.numTimesLoggedIn = payload.numTimesLoggedIn;
    state.numTimesLoggedOut = payload.numTimesLoggedOut;
    state.yearBorn = payload.yearBorn;
    state.country = payload.country;
    state.overrideOsDarkMode = payload.overrideOsDarkMode;
    state.playSoundEffects = payload.playSoundEffects;
    state.prefersColorScheme = payload.prefersColorScheme;
    state.customAvatarFilename = payload.customAvatarFilename;
    state.customAvatarOrientation = payload.customAvatarOrientation;
    state.customAvatarOriginalFilesizeKb = payload.customAvatarOriginalFilesizeKb;
    state.customAvatarOriginalHeight = payload.customAvatarOriginalHeight;
    state.customAvatarOriginalWidth = payload.customAvatarOriginalWidth;
    state.customAvatarUrl = payload.customAvatarUrl;
    updateUserDataInFirebase(payload);
  },
  SET_PREFERS_COLOR_SCHEME_TEMP_MUTATION: (state, payload) => {
    state.prefersColorSchemeTemp = payload.prefersColorSchemeTemp;
  },
  SET_NUM_TIMES_LOGGED_IN_INCREMENT_MUTATION: (state, payload) => {
    state.numTimesLoggedIn = state.numTimesLoggedIn + 1;
    updateUserDataInFirebase({
      numTimesLoggedIn: firebase.firestore.FieldValue.increment(1),
    });
  },
  SET_GAMERTAG_MUTATION: (state, payload) => {
    state.gamerTag = payload.gamerTag;
    updateUserDataInFirebase(payload);
  },
  SET_ADD_STARRED_BOARD_MUTATION: (state, payload) => {
    state.starredBoards.push(payload.boardId);
    state.starredBoards = uniq(state.starredBoards);
    updateUserDataInFirebase({
      starredBoards: state.starredBoards,
    });
  },
  SET_REMOVE_STARRED_BOARD_MUTATION: (state, payload) => {
    state.starredBoards = state.starredBoards.filter(boardId => boardId !== payload.boardId);
    updateUserDataInFirebase({
      starredBoards: state.starredBoards,
    });
  },
};

const actions = {
  SET_USER_DATA_FROM_FIREBASE_ACTION: ({commit, state}, payload) => {
    commit('SET_USER_DATA_FROM_FIREBASE_MUTATION', payload);
  },
  SET_LAST_LOGGED_IN_AT_ACTION: ({commit, state}, payload) => {
    const timestampObj = getTimestampObject();
    commit('SET_LAST_LOGGED_IN_AT_MUTATION', timestampObj);
  },
  SET_LAST_LOGGED_OUT_AT_ACTION: ({commit, state}, payload) => {
    const timestampObj = getTimestampObject();
    commit('SET_LAST_LOGGED_OUT_AT_MUTATION', timestampObj);
  },
  SET_USER_PROFILE_DATA_ACTION: ({commit, state}, payload) => {
    commit('SET_USER_PROFILE_DATA_MUTATION', payload);
  },
  CLEAR_USER_DATA_ACTION: ({commit, state}, payload) => {
    commit('CLEAR_USER_DATA_MUTATION', initState);
  },
  SET_PREFERS_COLOR_SCHEME_TEMP_ACTION: ({commit, state}, payload) => {
    commit('SET_PREFERS_COLOR_SCHEME_TEMP_MUTATION', payload);
  },
  SET_EMAIL_VERIFIED_ACTION: ({commit, state}, payload) => {
    commit('SET_EMAIL_VERIFIED_MUTATION', payload);
  },
  SET_NUM_TIMES_LOGGED_IN_INCREMENT_ACTION: ({commit, state}, payload) => {
    commit('SET_NUM_TIMES_LOGGED_IN_INCREMENT_MUTATION', payload);
  },
  SET_GAMERTAG_ACTION: ({commit, state}, payload) => {
    commit('SET_GAMERTAG_MUTATION', payload);
  },
  SET_ADD_STARRED_BOARD_ACTION: ({commit, state}, payload) => {
    commit('SET_ADD_STARRED_BOARD_MUTATION', payload);
  },
  SET_REMOVE_STARRED_BOARD_ACTION: ({commit, state}, payload) => {
    commit('SET_REMOVE_STARRED_BOARD_MUTATION', payload);
  },
};

export default {
  namespaced,
  state,
  getters,
  mutations,
  actions,
};
